/**
 * LanguageContext — dashboard-only ES/EN toggle (separate from the public
 * Homepage's own ES/EN/PT switcher, which has its own independent i18n).
 *
 * Design: Spanish stays inline as the fallback at every call site instead of
 * living in a parallel dictionary. `t('some.key', 'Texto en español')`
 * returns the English dictionary entry for 'some.key' when lang is 'en',
 * otherwise it returns the Spanish fallback untouched. This means retrofitting
 * translation onto already-approved Spanish UI never risks losing or
 * mistyping the existing text — only English needs to be authored, in
 * `dashboardEn.js`.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { EN_DICTIONARY } from '../translations/dashboardEn';

const LanguageContext = createContext(null);

const STORAGE_KEY = 'contact_dashboard_lang';

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'en' ? 'en' : 'es';
    } catch {
      return 'es';
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
  }, [lang]);

  const setLang = useCallback((next) => {
    setLangState(next === 'en' ? 'en' : 'es');
  }, []);

  const t = useCallback((key, esFallback) => {
    if (lang === 'en') {
      const entry = EN_DICTIONARY[key];
      if (entry !== undefined) return entry;
    }
    return esFallback;
  }, [lang]);

  const value = { lang, setLang, t };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside <LanguageProvider>');
  return ctx;
};

export default LanguageContext;
